import React, { useRef, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";

function DNAStrand({ dark, strandIndex }) {
  const group = useRef();
  const particles = useRef();
  
  useFrame(({ clock }) => {
    const time = clock.getElapsedTime();
    
    if (group.current) {
      group.current.rotation.y = time * 0.3;
      group.current.position.y = Math.sin(time * 0.5) * 0.5;
    }
    
    if (particles.current) {
      particles.current.children.forEach((particle, i) => {
        const angle = (i / 20) * Math.PI * 2 + time * 0.5;
        const radius = 2;
        const height = (i / 20) * 8 - 4;
        
        particle.position.x = Math.cos(angle + strandIndex * Math.PI) * radius;
        particle.position.y = height;
        particle.position.z = Math.sin(angle + strandIndex * Math.PI) * radius;
        
        particle.rotation.y = time * 0.5 + i * 0.1;
        particle.rotation.x = time * 0.3 + i * 0.05;
      });
    }
  });

  const particleColors = dark 
    ? ["#a5b4fc", "#818cf8", "#6366f1", "#4f46e5", "#e6e9f5"]
    : ["#1b1c4e", "#23244d", "#3730a3", "#4338ca", "#6366f1"];

  return (
    <group ref={group}>
      <group ref={particles}>
        {Array.from({ length: 40 }).map((_, i) => (
          <mesh key={i} position={[0, 0, 0]}>
            <sphereGeometry args={[0.08, 8, 8]} />
            <meshBasicMaterial 
              color={particleColors[i % particleColors.length]}
              opacity={0.7} 
              transparent 
            />
          </mesh>
        ))}
      </group>
      
      {/* Connecting lines */}
      {Array.from({ length: 39 }).map((_, i) => (
        <mesh key={`line-${i}`} position={[0, 0, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 0.4, 8]} />
          <meshBasicMaterial 
            color={dark ? "#6366f1" : "#1b1c4e"}
            opacity={0.4} 
            transparent 
          />
        </mesh>
      ))}
    </group>
  );
}

function FloatingMolecules({ dark }) {
  const molecules = useRef();
  
  useFrame(({ clock }) => {
    if (molecules.current) {
      molecules.current.children.forEach((molecule, i) => {
        const time = clock.getElapsedTime();
        molecule.position.x = Math.sin(time * 0.2 + i) * 15;
        molecule.position.y = Math.cos(time * 0.15 + i) * 8;
        molecule.position.z = Math.sin(time * 0.3 + i) * 4;
        molecule.rotation.y = time * 0.5 + i;
        molecule.rotation.x = time * 0.3 + i;
      });
    }
  });

  return (
    <group ref={molecules}>
      {Array.from({ length: 12 }).map((_, i) => (
        <group key={i} position={[
          (Math.random() - 0.5) * 30,
          (Math.random() - 0.5) * 16,
          (Math.random() - 0.5) * 8
        ]}>
          {/* Central atom */}
          <mesh>
            <sphereGeometry args={[0.15, 16, 16]} />
            <meshBasicMaterial 
              color={dark ? "#a5b4fc" : "#1b1c4e"}
              opacity={0.6} 
              transparent 
            />
          </mesh>
          
          {/* Orbital electrons */}
          {Array.from({ length: 4 }).map((_, j) => (
            <mesh key={j} position={[
              Math.cos(j * Math.PI / 2) * 0.3,
              Math.sin(j * Math.PI / 2) * 0.3,
              0
            ]}>
              <sphereGeometry args={[0.05, 8, 8]} />
              <meshBasicMaterial 
                color={dark ? "#818cf8" : "#23244d"}
                opacity={0.8} 
                transparent 
              />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

export default function DNAHelix({ dark, className = "" }) {
  return (
    <div className={`fixed inset-0 pointer-events-none ${className}`} style={{ zIndex: 3 }}>
      <Canvas camera={{ position: [0, 0, 8], fov: 60 }}>
        <ambientLight intensity={0.4} />
        <pointLight position={[10, 10, 10]} intensity={0.8} />
        
        {/* DNA Strands */}
        <DNAStrand dark={dark} strandIndex={0} />
        <DNAStrand dark={dark} strandIndex={1} />
        
        {/* Floating Molecules */}
        <FloatingMolecules dark={dark} />
      </Canvas>
    </div>
  );
}

export function SectionDNA({ dark, className = "" }) {
  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
        <ambientLight intensity={0.5} />
        <DNAStrand dark={dark} strandIndex={0} />
        <FloatingMolecules dark={dark} />
      </Canvas>
    </div>
  );
} 